import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { DEFAULT_TIMEZONE } from '../constants.js';
import { isCountryCode } from '../countries.js';
import { email, isoDate, uuid } from './common.js';

/**
 * Pre-race profile (P-11) and medical certificate (P-12).
 * Validation messages are i18n keys (`profile.error.*`): the SPA translates them, the API
 * returns them in `details`.
 */

export const documentKind = z.enum(['medical_certificate', 'pps_attestation', 'licence']);
/**
 * `pending_upload`: the row and the upload URL exist, the file is not confirmed yet.
 * `pending_review`: uploaded and checked, waiting for the organiser (A-05).
 */
export const documentStatus = z.enum(['pending_upload', 'pending_review', 'approved', 'rejected']);
export type DocumentStatus = z.infer<typeof documentStatus>;

export const GENDERS = ['f', 'm', 'x'] as const;
export const gender = z.enum(GENDERS);
export const EMERGENCY_RELATIONSHIPS = [
  'partner',
  'parent',
  'child',
  'sibling',
  'friend',
  'other',
] as const;
export const emergencyRelationship = z.enum(EMERGENCY_RELATIONSHIPS);
/** The licence that replaces a medical certificate (PRD P-11): cycling or athletics federation. */
export const LICENCE_FEDERATIONS = ['FFC', 'FFA'] as const;
export const licenceFederation = z.enum(LICENCE_FEDERATIONS);
export const licenceStatus = z.enum(['pending_review', 'approved', 'rejected']);
export type LicenceStatus = z.infer<typeof licenceStatus>;
export const DEFAULT_PHONE_COUNTRY = 'FR';
export const DEFAULT_ADDRESS_COUNTRY = 'FR';

/** A phone number in E.164 (`+33612345678`), or null when it is not a valid number. */
export function normalizePhone(input: string, defaultCountry = DEFAULT_PHONE_COUNTRY) {
  const parsed = parsePhoneNumberFromString(input.trim(), defaultCountry as never);
  return parsed?.isValid() ? (parsed.number as string) : null;
}

/** `YYYY-MM-DD`, a real calendar day, after 1900-01-01 and before `today` (UTC). */
export function isValidBirthDate(value: string, today = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== value) return false;
  return value >= '1900-01-01' && value < today.toISOString().slice(0, 10);
}

/** French postcodes are 5 digits; elsewhere 2–10 letters, digits, spaces or dashes. */
export function isValidPostalCode(postalCode: string, country: string | null | undefined) {
  const v = postalCode.trim();
  return (country ?? DEFAULT_ADDRESS_COUNTRY) === 'FR'
    ? /^\d{5}$/.test(v)
    : /^[A-Za-z0-9][A-Za-z0-9 -]{0,8}[A-Za-z0-9]$/.test(v);
}

const blankToNull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);
/** Every field of the PATCH accepts null (or an empty string) to clear it. */
const clearable = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(blankToNull, schema.nullable());

const required = 'profile.error.required';
const personName = z.string().trim().min(1, required).max(80, 'profile.error.tooLong');
export const phoneNumber = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const e164 = normalizePhone(v);
    if (!e164) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'profile.error.phone' });
      return z.NEVER;
    }
    return e164;
  });
export const birthDate = z
  .string()
  .trim()
  .refine((v) => isValidBirthDate(v), 'profile.error.date');
export const isoCountry = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => isCountryCode(v), 'profile.error.country');

/** Postal address as stored (`users.address_json`); parts may be missing while typing. */
export const postalAddress = z.object({
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
});
export type PostalAddress = z.infer<typeof postalAddress>;

/** Address parts to change, merged by the API into the stored address (autosave per field). */
export const addressPatch = z
  .object({
    line1: clearable(z.string().trim().max(120, 'profile.error.tooLong')),
    line2: clearable(z.string().trim().max(120, 'profile.error.tooLong')),
    // Checked against the address's country once merged (`profileConflicts`).
    postalCode: clearable(z.string().trim().max(12, 'profile.error.postalCode')),
    city: clearable(z.string().trim().max(80, 'profile.error.tooLong')),
    country: isoCountry,
  })
  .partial()
  .strict();
export type AddressPatch = z.infer<typeof addressPatch>;

/**
 * `PATCH /me/profile`: any subset of the fields, each saved as soon as it is left (P-11).
 * Used as is by the API validator and, field by field, by the profile form.
 */
export const updateProfileInput = z
  .object({
    firstName: clearable(personName),
    lastName: clearable(personName),
    birthDate: clearable(birthDate),
    gender: clearable(gender),
    nationality: clearable(isoCountry),
    phone: clearable(phoneNumber),
    address: addressPatch,
    clubName: clearable(z.string().trim().max(120, 'profile.error.tooLong')),
    emergencyContactName: clearable(
      z.string().trim().min(1, required).max(120, 'profile.error.tooLong'),
    ),
    emergencyContactPhone: clearable(phoneNumber),
    emergencyContactRelationship: clearable(emergencyRelationship),
    licenceFederation: clearable(licenceFederation),
    licenceNumber: clearable(
      z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9][A-Za-z0-9 -]{2,23}$/, 'profile.error.licenceNumber'),
    ),
  })
  .partial()
  .strict()
  .refine((p) => Object.keys(p).length > 0, 'profile.error.empty');
export type UpdateProfileInput = z.infer<typeof updateProfileInput>;
export type ProfileField = keyof UpdateProfileInput;

/** The profile as stored and returned by the API. */
export const profileFields = z.object({
  email,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  birthDate: z.string().nullable(),
  gender: gender.nullable(),
  nationality: z.string().nullable(),
  phone: z.string().nullable(),
  address: postalAddress.nullable(),
  clubName: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
  emergencyContactRelationship: emergencyRelationship.nullable(),
  licenceFederation: licenceFederation.nullable(),
  licenceNumber: z.string().nullable(),
  /**
   * The organiser's review of the licence (read only): `pending_review` as soon as it is
   * entered or changed; only `approved` makes the documents complete. Null without a licence.
   */
  licenceStatus: licenceStatus.nullable(),
  licenceRejectionReason: z.string().nullable(),
});
export type ProfileFields = z.infer<typeof profileFields>;

/** Merge a PATCH into the stored profile (address parts merged one by one). */
export function mergeProfile<T extends Omit<ProfileFields, 'email'>>(
  current: T,
  patch: UpdateProfileInput,
): T {
  const { address, ...rest } = patch;
  const next = { ...current, ...rest } as T;
  if (address) {
    next.address = {
      line1: null,
      line2: null,
      postalCode: null,
      city: null,
      country: DEFAULT_ADDRESS_COUNTRY,
      ...current.address,
      ...address,
    };
  }
  // A new or changed licence goes (back) to the organiser's review; a cleared one has none.
  if (
    next.licenceFederation !== current.licenceFederation ||
    next.licenceNumber !== current.licenceNumber
  ) {
    next.licenceStatus = hasLicence(next) ? 'pending_review' : null;
    next.licenceRejectionReason = null;
  }
  return next;
}

/**
 * Rules across fields, checked on the merged profile by the API and the form:
 * the emergency contact must not be the participant's own phone; the postcode follows the
 * address's country. Returns field → i18n error key.
 */
export function profileConflicts(
  p: Pick<ProfileFields, 'phone' | 'emergencyContactPhone' | 'address'>,
): Partial<Record<'emergencyContactPhone' | 'postalCode', string>> {
  const errors: Partial<Record<'emergencyContactPhone' | 'postalCode', string>> = {};
  if (p.phone && p.emergencyContactPhone && p.phone === p.emergencyContactPhone) {
    errors.emergencyContactPhone = 'profile.error.emergencySamePhone';
  }
  if (p.address?.postalCode && !isValidPostalCode(p.address.postalCode, p.address.country)) {
    errors.postalCode = 'profile.error.postalCode';
  }
  return errors;
}

/** `YYYY-MM-DD`. */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'profile.error.date');

export const documentRecord = z.object({
  id: uuid,
  kind: documentKind,
  status: documentStatus,
  /** Date written on the certificate, as declared by the participant. */
  issuedOn: isoDay.nullable(),
  /** Last day the certificate is valid (issue + 1 year − 1 day, or set at review). */
  expiresOn: isoDay.nullable(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: isoDate,
  reviewedAt: isoDate.nullable(),
  rejectionReason: z.string().nullable(),
});
export type DocumentRecord = z.infer<typeof documentRecord>;

export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Accepted certificate files (P-12) and the extension of their R2 key. */
export const DOCUMENT_CONTENT_TYPES = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
} as const;
export const documentContentType = z.enum(['application/pdf', 'image/jpeg', 'image/png'], {
  message: 'profile.error.fileType',
});
/** Presigned PUT and GET lifetime (CLAUDE.md invariant 8). */
export const DOCUMENT_URL_TTL_SECONDS = 10 * 60;

/**
 * A medical certificate is valid for one year from the date the doctor wrote on it (French
 * sports rules: "datant de moins d'un an" on race day). It is global to the platform: one valid
 * certificate covers every event whose race day falls before it expires.
 */
export const CERTIFICATE_VALIDITY_MONTHS = 12;

/** Last valid day of a certificate issued on `issuedOn` (`YYYY-MM-DD`): one year minus a day. */
export function certificateExpiresOn(issuedOn: string): string {
  const d = new Date(`${issuedOn}T00:00:00Z`);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + CERTIFICATE_VALIDITY_MONTHS;
  // Day 29 Feb + 1 year → 1 Mar, minus a day → 28 Feb: JS date overflow does the right thing.
  const end = new Date(Date.UTC(y, m, d.getUTCDate()) - 86_400_000);
  return end.toISOString().slice(0, 10);
}

/** Today in the event time zone, `YYYY-MM-DD`. */
export function dayIn(date: Date, timeZone = DEFAULT_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * The day a certificate must still be valid on for an event: its race day, or today once the
 * race day is past or unknown. An expired certificate is never accepted.
 */
export function certificateCoverDay(eventStartsAt: Date | null, now = new Date()): string {
  const today = dayIn(now);
  const race = eventStartsAt ? dayIn(eventStartsAt) : today;
  return race > today ? race : today;
}

/**
 * The participant's certificate as it counts for an event (P-12). `expired`: approved, but no
 * longer valid on the cover day (or without an expiry date), so a new one is required.
 */
export const certificateState = z.enum([
  'none',
  'pending_review',
  'approved',
  'expired',
  'rejected',
]);
export type CertificateState = z.infer<typeof certificateState>;

export function certificateStateOf(
  cert: { status: DocumentStatus; expiresOn: string | null } | null,
  coverDay: string,
): CertificateState {
  if (!cert || cert.status === 'pending_upload') return 'none';
  if (cert.status === 'approved') {
    return cert.expiresOn && cert.expiresOn >= coverDay ? 'approved' : 'expired';
  }
  return cert.status;
}

/** `POST /me/documents/upload-url`: declared file, checked before anything is signed. */
export const documentUploadInput = z.object({
  /** The date written on the certificate; it sets the expiry date. */
  issuedOn: z
    .string()
    .trim()
    .refine(
      (v) => isValidBirthDate(v, new Date(Date.now() + 86_400_000)),
      'profile.error.certificateDate',
    ),
  fileName: z.string().trim().min(1).max(200),
  contentType: documentContentType,
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(DOCUMENT_MAX_BYTES, { message: 'profile.error.fileTooLarge' }),
});
export type DocumentUploadInput = z.infer<typeof documentUploadInput>;

export const documentUploadOutput = z.object({
  document: documentRecord,
  uploadUrl: z.string().url(),
  /** Headers the PUT must send exactly (the content type is part of the signature). */
  headers: z.record(z.string()),
  expiresAt: isoDate,
});
export type DocumentUploadOutput = z.infer<typeof documentUploadOutput>;

export const documentUrlOutput = z.object({ url: z.string().url(), expiresAt: isoDate });
export type DocumentUrlOutput = z.infer<typeof documentUrlOutput>;

// ---------------------------------------------------------------------------
// Completion (checklist + progress bar of screen 06)
// ---------------------------------------------------------------------------

export const checklistKey = z.enum(['identity', 'contact', 'emergency', 'documents', 'club']);
export type ChecklistKey = z.infer<typeof checklistKey>;
/**
 * `done`; `todo`; `submitted`: certificate or licence waiting for review (shown as sent, not
 * complete); `rejected`: certificate or licence refused, to supply again; `expired`: the approved certificate is no
 * longer valid on race day, a new one is required; `optional`: club left empty.
 */
export const checklistState = z.enum([
  'done',
  'todo',
  'submitted',
  'rejected',
  'expired',
  'optional',
]);
export type ChecklistState = z.infer<typeof checklistState>;

export const checklistItem = z.object({
  key: checklistKey,
  state: checklistState,
  /** Fields still empty in this item (for the hint under its title). */
  missing: z.array(z.string()),
});
export type ChecklistItem = z.infer<typeof checklistItem>;

export const profileCompletion = z.object({
  /** 0–100: required items, each weighted equally, partial credit for its filled fields. */
  percent: z.number().int().min(0).max(100),
  /** Every required personal field filled (club and documents excluded): `PROFILE_COMPLETE`. */
  profileComplete: z.boolean(),
  /**
   * Licence approved, or a certificate approved and still valid on race day, or no document
   * required: `DOCS_COMPLETE`.
   */
  docsComplete: z.boolean(),
  documentsRequired: z.boolean(),
  items: z.array(checklistItem),
});
export type ProfileCompletion = z.infer<typeof profileCompletion>;

export const profileResponse = z.object({
  profile: profileFields,
  /**
   * The participant's latest certificate, whichever event it was uploaded for (the profile is
   * global); null when none.
   */
  certificate: documentRecord.nullable(),
  /** How that certificate counts for the current event (expiry checked against race day). */
  certificateState,
  completion: profileCompletion,
});
export type ProfileResponse = z.infer<typeof profileResponse>;

/** A licence is entered once both its federation and its number are (P-11). */
export function hasLicence(p: {
  licenceFederation: string | null;
  licenceNumber: string | null;
}): boolean {
  return Boolean(p.licenceFederation && p.licenceNumber?.trim());
}

type LicenceFields = Pick<ProfileFields, 'licenceFederation' | 'licenceNumber' | 'licenceStatus'>;

/** The licence's review state, `none` without a licence. */
export function licenceStateOf(p: LicenceFields): LicenceStatus | 'none' {
  if (!hasLicence(p)) return 'none';
  return p.licenceStatus ?? 'pending_review';
}

/**
 * `DOCS_COMPLETE` (PRD §4.8): a licence approved by the organiser, or a certificate approved
 * and still valid. Entering a licence alone is not enough: it is reviewed first.
 */
export function documentsSupplied(p: LicenceFields, certificate: CertificateState): boolean {
  return licenceStateOf(p) === 'approved' || certificate === 'approved';
}

const filled = (v: string | null | undefined) => Boolean(v && v.trim());

/**
 * The one completion rule (P-11), used by the API for the profile, the registrations'
 * `profile_complete` / `documents_approved` flags and the Brevo attributes. Pure.
 */
export function computeCompletion(input: {
  profile: Omit<ProfileFields, 'email'>;
  /** From `certificateStateOf`: the expiry is already checked against the event. */
  certificate: CertificateState;
  documentsRequired: boolean;
}): ProfileCompletion {
  const p = input.profile;
  const a = p.address;
  const groups: {
    key: Exclude<ChecklistKey, 'documents' | 'club'>;
    fields: [string, boolean][];
  }[] = [
    {
      key: 'identity',
      fields: [
        ['firstName', filled(p.firstName)],
        ['lastName', filled(p.lastName)],
        ['birthDate', filled(p.birthDate)],
        ['gender', filled(p.gender)],
        ['nationality', filled(p.nationality)],
      ],
    },
    {
      key: 'contact',
      fields: [
        ['phone', filled(p.phone)],
        ['address', Boolean(a && filled(a.line1) && filled(a.postalCode) && filled(a.city))],
      ],
    },
    {
      key: 'emergency',
      fields: [
        ['emergencyContactName', filled(p.emergencyContactName)],
        ['emergencyContactPhone', filled(p.emergencyContactPhone)],
        ['emergencyContactRelationship', filled(p.emergencyContactRelationship)],
      ],
    },
  ];

  const items: ChecklistItem[] = [];
  let score = 0;
  for (const g of groups) {
    const missing = g.fields.filter(([, ok]) => !ok).map(([k]) => k);
    score += (g.fields.length - missing.length) / g.fields.length;
    items.push({ key: g.key, state: missing.length ? 'todo' : 'done', missing });
  }
  const profileComplete = items.every((i) => i.state === 'done');

  const supplied = documentsSupplied(p, input.certificate);
  const licence = licenceStateOf(p);
  if (input.documentsRequired) {
    // Something waiting for review wins over an earlier refusal or an expired certificate.
    const state: ChecklistState = supplied
      ? 'done'
      : input.certificate === 'pending_review' || licence === 'pending_review'
        ? 'submitted'
        : input.certificate === 'rejected' || licence === 'rejected'
          ? 'rejected'
          : input.certificate === 'expired'
            ? 'expired'
            : 'todo';
    if (supplied) score += 1;
    items.push({ key: 'documents', state, missing: supplied ? [] : ['documents'] });
  }
  const requiredCount = items.length;
  items.push({ key: 'club', state: filled(p.clubName) ? 'done' : 'optional', missing: [] });

  return {
    percent: Math.round((score / requiredCount) * 100),
    profileComplete,
    docsComplete: supplied || !input.documentsRequired,
    documentsRequired: input.documentsRequired,
    items,
  };
}

/**
 * `POST /admin/documents/:id/review` (A-05): a rejection always carries its reason; an approval
 * keeps the declared expiry unless the organiser corrects it.
 */
export const reviewDocumentInput = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('approved'),
    /** Corrects the expiry declared with the upload (last valid day), if the file says otherwise. */
    expiresOn: isoDay.optional(),
  }),
  z.object({ status: z.literal('rejected'), reason: z.string().trim().min(1).max(500) }),
]);
export type ReviewDocumentInput = z.infer<typeof reviewDocumentInput>;

/** `POST /admin/users/:id/licence/review` (A-05): the licence entered on the profile. */
export const reviewLicenceInput = z.discriminatedUnion('status', [
  z.object({ status: z.literal('approved') }),
  z.object({ status: z.literal('rejected'), reason: z.string().trim().min(1).max(500) }),
]);
export type ReviewLicenceInput = z.infer<typeof reviewLicenceInput>;
